import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import CustomerBottomNav from '../../components/common/CustomerBottomNav';
import { toast } from 'sonner';
import { 
  ArrowLeft, Gift, Copy, Share2, Users, Wallet, CheckCircle
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Referral() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch referral code
        const codeRes = await axios.get(`${API}/referral/my-code`, { headers });
        setReferralData(codeRes.data);
        
        // Fetch program settings
        const settingsRes = await axios.get(`${API}/referral/settings`);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Failed to fetch referral data:', error);
        toast.error('Failed to load referral information');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const copyCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      toast.success('Referral code copied!');
    }
  };

  const shareCode = async () => {
    if (navigator.share && referralData?.code) {
      try {
        await navigator.share({
          title: 'Join HandyBayani!',
          text: `Use my referral code ${referralData.code} when you sign up and we both get Php ${settings?.reward_amount || 100}!`,
          url: `${window.location.origin}/register?ref=${referralData.code}`
        });
      } catch (e) {
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!settings?.enabled) {
    return (
      <div className="min-h-screen bg-stone-50 pb-20" data-testid="referral-page">
        <header className="bg-slate-900 pt-4 pb-4 px-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-white font-bold text-lg font-[Poppins]">Refer & Earn</h1>
          </div>
        </header>
        <main className="px-4 py-8">
          <div className="text-center py-12">
            <Gift className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Referral Program Unavailable</h2>
            <p className="text-slate-500">The referral program is currently not active. Check back later!</p>
          </div>
        </main>
        <CustomerBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-20" data-testid="referral-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-500 to-amber-400 pt-4 pb-8 px-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-slate-900" />
          </button>
          <h1 className="text-slate-900 font-bold text-lg font-[Poppins]">Refer & Earn</h1>
        </div>
        
        <div className="text-center">
          <Gift className="w-16 h-16 text-slate-900/80 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-slate-900">Earn Php {settings?.reward_amount || 100}</h2>
          <p className="text-slate-700 mt-1">for every friend you refer!</p>
        </div>
      </header>

      <main className="px-4 -mt-4">
        {/* Referral Code Card */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
          <p className="text-sm text-slate-500 text-center mb-2">Your Referral Code</p>
          <div className="flex items-center justify-center gap-3">
            <div className="px-6 py-3 bg-slate-100 rounded-xl">
              <span className="text-2xl font-bold font-mono tracking-wider text-slate-900" data-testid="referral-code">
                {referralData?.code || 'LOADING'}
              </span>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={copyCode}
              className="h-12 w-12"
              data-testid="copy-code-btn"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>
          
          <Button
            className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900 h-12"
            onClick={shareCode}
            data-testid="share-code-btn"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Share Your Code
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{referralData?.total_referrals || 0}</p>
                <p className="text-xs text-slate-500">Total Referrals</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Php {(referralData?.total_earnings || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                <p className="text-xs text-slate-500">Total Earned</p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h3 className="font-bold text-slate-900 mb-4">How It Works</h3>
          
          <div className="space-y-4">
            {(settings?.mechanics || [
              `Share your unique referral code with friends and family.`,
              `Your recruit signs up using your referral code.`,
              `Your recruit must complete ${settings?.required_bookings || 5} successful paid bookings.`,
              `Once completed, both you and your recruit receive Php ${settings?.reward_amount || 100} in your wallets.`
            ]).map((step, index) => (
              <div key={`step-${index + 1}`} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-slate-900">{index + 1}</span>
                </div>
                <p className="text-sm text-slate-600">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Program Description */}
        {settings?.description && (
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
            <p className="text-sm text-amber-800">{settings.description}</p>
          </div>
        )}
      </main>

      <CustomerBottomNav />
    </div>
  );
}
